/**
 * Waiting for a screen to be ready before a tour starts.
 *
 * WHY A TOUR CANNOT JUST START
 *
 * Joyride looks for the first step's target exactly once. If it is not in the
 * document, the tour sits at lifecycle "init" forever — it does not retry, and
 * it does not even emit target_not_found, so no error branch can rescue it. A
 * tour started on a tab that has not painted yet is simply a tour that never
 * appears. That was unreachable while the only way in was a button on the screen
 * being described, and certain the moment tours could be started from the header
 * on a tab you are not on yet.
 *
 * Every step AFTER the first is recoverable: target_not_found fires and the
 * hook's error branch skips it. So the first step is the only one worth waiting
 * for, and "filter the whole step list against the DOM before starting" is the
 * wrong instrument — four hooks did exactly that, computed the filtered array,
 * tested it for emptiness, and then handed Joyride the unfiltered list anyway.
 *
 * Extracted from useSimpleTour, which had the only correct copy, so the four
 * hand-rolled hooks could stop carrying a broken one each.
 */

//How long to wait before giving up. Long enough for a tab switch plus a fetch.
export const READY_TIMEOUT_MS = 4000;

/**
 * Poll until the screen is ready, then run `start`.
 *
 * @param wait   a CSS selector, a predicate returning truthy when ready, or
 *               nullish to start immediately.
 * @param start  called once, when ready. Not called at all if we give up.
 * @param delay  how long to wait before the first check.
 */
export function whenReady(wait, start, { delay = 100 } = {}) {
    const isReady = () => {
        if (!wait) return true;
        if (typeof wait === 'function') return !!wait();
        return !!document.querySelector(wait);
    };

    const deadline = Date.now() + READY_TIMEOUT_MS;
    const tick = () => {
        if (isReady()) { start(); return; }
        if (Date.now() > deadline) {
            //Not starting beats starting into a tour that cannot advance.
            console.warn('Tour: screen never became ready; not starting.', wait);
            return;
        }
        setTimeout(tick, 100);
    };
    setTimeout(tick, delay);
}
